/**
 * Animal Health — self-explanatory help copy + `<InfoButton />` component.
 * =========================================================================
 * Central catalogue of the tooltips/popovers surfaced next to every field
 * on the Eligible Cohort Workspace and Deep Dive modal. All copy lives
 * here so a) it's discoverable in one file and b) any wording tweaks
 * flow through both screens instantly.
 */
import React from 'react';
import { Info } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';

/* ── Reusable info button ─────────────────────────────────────── */
export const InfoButton = ({ title, body, size = 'sm', side = 'top', align = 'start', testId }) => {
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`About: ${title}`}
          className="inline-flex items-center justify-center text-slate-400 hover:text-indigo-600 transition align-middle"
          onClick={(e) => e.stopPropagation()}
          data-testid={testId || `info-btn-${(title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        >
          <Info className={iconSize} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className="w-[360px] text-[11.5px] leading-relaxed text-slate-700 p-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[12px] font-semibold text-slate-900 mb-1.5">{title}</div>
        {typeof body === 'string' ? <p>{body}</p> : body}
      </PopoverContent>
    </Popover>
  );
};

/* ── Page-level help ──────────────────────────────────────────── */
export const HELP = {
  workspace: {
    title: 'Eligible Cohort Workspace',
    body: (
      <div className="space-y-1.5">
        <p>
          Every asset that PASSES your Animal Health screening rules
          (Development Stage include-list AND at least one Therapeutic
          Area disease chip) lands here.
        </p>
        <p>The workspace evolves in three phases:</p>
        <ol className="list-decimal pl-4 space-y-0.5">
          <li><b>Before AI</b> — Pending list. Click <em>Run AI on Cohort</em>.</li>
          <li><b>During AI</b> — Live progress panel; results appear as each asset finishes.</li>
          <li><b>After AI</b> — Tier cards + Deep Dive per row.</li>
        </ol>
      </div>
    ),
  },
  screeningRules: {
    title: 'How the Eligible Cohort is built',
    body: (
      <div className="space-y-1.5">
        <p><b>Universe:</b> Every human pharma / biotech asset in the platform.</p>
        <p><b>Include-only filters (all must pass):</b></p>
        <ul className="list-disc pl-4 space-y-0.5">
          <li><b>Development Stage</b> — asset stage/phase must be in the include-list you defined on the Screening Rules page.</li>
          <li><b>Therapeutic Area</b> — asset must map to at least one selected TA <em>and</em> its indication must match an enabled disease chip in that TA.</li>
        </ul>
        <p><b>Exclusions (auto-removed):</b> Platform-only technologies (no drug), diagnostic devices, delivery platforms, and assets without a licensable molecule.</p>
      </div>
    ),
  },
  runAiCohort: {
    title: 'Run AI on Cohort',
    body: (
      <div className="space-y-1.5">
        <p>Queues every pending asset for evaluation by Claude Sonnet 4.5. Three assets are scored in parallel to balance throughput vs. rate limits.</p>
        <p>Each asset is scored on <b>10 criteria × 10 points = 100</b>. Results stream in live — no need to wait for the whole batch.</p>
      </div>
    ),
  },
  exportCsv: {
    title: 'Export CSV',
    body: 'Exports the currently filtered cohort with asset, company, phase, TA, AI Status, AI Score, AI Confidence, Decision, and Manual Decision. Order matches the visible sort.',
  },

  /* ── Column headers ─────────────────────────────────────────── */
  aiStatus: {
    title: 'AI Status',
    body: (
      <div className="space-y-1">
        <p>Where each asset sits in the evaluation pipeline:</p>
        <ul className="list-disc pl-4 space-y-0.5">
          <li><b>Pending</b> — awaiting AI evaluation.</li>
          <li><b>Queued</b> — added to the batch, not yet started.</li>
          <li><b>Running</b> — Claude is scoring right now.</li>
          <li><b>Completed</b> — score + rationale saved.</li>
          <li><b>Failed</b> — evaluation errored. Click <em>Retry</em>.</li>
        </ul>
      </div>
    ),
  },
  aiScore: {
    title: 'AI Opportunity Score (0–100)',
    body: (
      <div className="space-y-1.5">
        <p><b>What it measures:</b> How attractive this asset is for repositioning into companion animal (dog/cat) health.</p>
        <p><b>How it&rsquo;s computed:</b> Simple SUM of 10 equally-weighted criteria (0–10 each). No weighting, no averaging.</p>
        <ol className="list-decimal pl-4 space-y-0.5 text-[10.5px]">
          <li>Animal Health Translation Potential</li>
          <li>Disease &amp; Unmet Need Fit</li>
          <li>Scientific Evidence &amp; Differentiation</li>
          <li>Development Readiness</li>
          <li>Commercial Opportunity</li>
          <li>Regulatory Feasibility</li>
          <li>IP &amp; Competitive Position</li>
          <li>Licensing &amp; Partner Readiness</li>
          <li>Strategic Buyer Alignment</li>
          <li>Overall Partnering Attractiveness</li>
        </ol>
        <p>Every criterion in the Deep Dive has a per-criterion rationale + supporting evidence.</p>
      </div>
    ),
  },
  aiConfidence: {
    title: 'AI Confidence (High / Medium / Low)',
    body: (
      <div className="space-y-1.5">
        <p><b>What it measures:</b> How much you should trust the score, based on the completeness and reliability of the evidence Claude had.</p>
        <p><b>It is NOT a second score.</b> It doesn&rsquo;t change the number — it tells you whether that number is well-supported.</p>
        <p><b>How it&rsquo;s derived:</b> Aggregated from the confidence Claude assigns to each of the 10 criteria:</p>
        <ul className="list-disc pl-4 space-y-0.5">
          <li><b>High</b> — ≥ 6 criteria rated High, ≤ 2 rated Low.</li>
          <li><b>Low</b> — ≥ 4 criteria rated Low.</li>
          <li><b>Medium</b> — everything in between.</li>
        </ul>
        <p><b>Read together with the score:</b> Score 84 + Low confidence = looks great but validate before acting. Score 62 + High confidence = solidly mid-tier, no surprises.</p>
      </div>
    ),
  },
  decision: {
    title: 'Decision Tier (auto-classified)',
    body: (
      <div className="space-y-1.5">
        <p>A label mapped from the AI Score using admin-configurable thresholds:</p>
        <ul className="list-disc pl-4 space-y-0.5">
          <li><b>Tier 1 — Pursue Now (80–100):</b> Strong candidate. Immediate deep review, partner mapping and outreach.</li>
          <li><b>Tier 2 — Advance / Monitor (60–79):</b> Promising with gaps. Deeper AI review, expert assessment or additional evidence.</li>
          <li><b>Tier 3 — Park / Pass (0–59):</b> Insufficiently attractive today. Revisit only if new evidence emerges.</li>
        </ul>
        <p>The Decision is the AI&rsquo;s recommendation. You can override it via <em>Manual Decision</em> in the Deep Dive — both are stored separately.</p>
      </div>
    ),
  },
  actions: {
    title: 'Row actions',
    body: (
      <div className="space-y-1">
        <ul className="list-disc pl-4 space-y-0.5">
          <li><b>Run AI</b> — evaluates this single asset (~15-30 sec).</li>
          <li><b>Running…</b> — in-flight, button disabled.</li>
          <li><b>Deep Dive</b> — opens the full evaluation modal.</li>
          <li><b>Retry</b> — appears on failed evaluations.</li>
        </ul>
      </div>
    ),
  },

  /* ── Tier cards ─────────────────────────────────────────────── */
  tierAll: {
    title: 'All eligible assets',
    body: 'Every asset that passed the screening rules — whether evaluated or still pending. Click to reset the tier filter.',
  },
  tierPursue: {
    title: 'Tier 1 — Pursue Now (80–100)',
    body: 'Strong candidates for immediate detailed review, partner mapping and potential outreach. Filter to this tier to see only your highest-conviction opportunities.',
  },
  tierAdvance: {
    title: 'Tier 2 — Advance / Monitor (60–79)',
    body: 'Promising candidates with evidence gaps or uncertainties. Prioritise these for deeper AI review, expert consult, or new evidence-gathering before committing BD resources.',
  },
  tierPass: {
    title: 'Tier 3 — Park / Pass (0–59)',
    body: 'Currently insufficiently attractive, poorly supported, or high-risk. Not prioritised unless significant new evidence emerges.',
  },

  /* ── Deep Dive fields ───────────────────────────────────────── */
  humanPrimaryIndication: {
    title: 'Human Primary Indication',
    body: 'The main disease the asset is being developed for in humans. Extracted directly from the company\'s public disclosures / pipeline pages.',
  },
  humanSecondaryIndication: {
    title: 'Human Secondary Indication',
    body: 'Additional indications the asset is being explored for in humans (Phase 1/2 expansion cohorts, label extensions, sub-populations). Optional field.',
  },
  humanTherapeuticArea: {
    title: 'Human Therapeutic Area',
    body: 'The broader human therapeutic area (e.g. Oncology, Immunology, Nephrology) the asset belongs to.',
  },
  developmentStage: {
    title: 'Development Stage',
    body: 'Current clinical development stage of the human asset (Preclinical → Phase 1 → Phase 2 → Phase 3 → Filed → Approved).',
  },
  matchedAhTa: {
    title: 'Matched Animal-Health TA',
    body: (
      <div className="space-y-1">
        <p>The companion-animal therapeutic area Claude mapped the human indication to.</p>
        <p>Examples: <em>Canine Oncology, Feline Dermatology, Canine Renal, Companion Animal Ophthalmology.</em></p>
        <p>Blank if Claude couldn&rsquo;t confidently map the indication (usually a Tier 3 asset).</p>
      </div>
    ),
  },
  matchedAhDisease: {
    title: 'Matched Animal-Health Disease',
    body: 'The specific companion animal disease Claude mapped the human indication to (e.g. Canine Lymphoma, Feline CKD, Canine Atopic Dermatitis). Blank if unclear.',
  },
  speciesRelevance: {
    title: 'Proposed Species Relevance',
    body: (
      <div className="space-y-1">
        <p>Which companion animal species the asset is most relevant for:</p>
        <ul className="list-disc pl-4 space-y-0.5">
          <li><b>Dog</b> — canine indication only.</li>
          <li><b>Cat</b> — feline indication only.</li>
          <li><b>Both</b> — relevant for both dogs and cats.</li>
          <li><b>Other</b> — livestock or exotic (rare here).</li>
          <li><b>Unclear</b> — Claude could not infer species from the indication.</li>
        </ul>
      </div>
    ),
  },
  modality: {
    title: 'Modality',
    body: 'The drug modality — Small Molecule, Monoclonal Antibody, Peptide, ADC, Gene Therapy, Vaccine, etc. Affects veterinary formulation feasibility.',
  },

  manualDecision: {
    title: 'Committee / Manual Decision',
    body: (
      <div className="space-y-1.5">
        <p>Your team&rsquo;s decision after reviewing the AI evaluation. Stored SEPARATELY from the AI recommendation — the AI Decision is never overwritten.</p>
        <ul className="list-disc pl-4 space-y-0.5">
          <li><b>Pursue Now / Advance / Park / Pass</b> — your call.</li>
          <li><b>Not Yet Reviewed</b> — default until someone opens the Deep Dive.</li>
        </ul>
        <p>When your decision differs from the AI recommendation, a text box appears so you can capture the rationale (audit trail).</p>
      </div>
    ),
  },
  keyStrengths: {
    title: 'Key Strengths',
    body: 'Claude\'s 3-5 bullet summary of the strongest signals across all 10 criteria. Copy these directly into a BD one-pager.',
  },
  keyRisks: {
    title: 'Key Risks / Evidence Gaps',
    body: 'Claude\'s 3-5 bullet summary of the weakest signals or missing evidence. Use these to plan the diligence questions for the next call.',
  },
  recommendedNextAction: {
    title: 'Recommended Next Action',
    body: 'Claude\'s single best next step for the BD team — e.g. "Request Phase 2 CSR", "Schedule call with target", "Request IP FTO summary". A quick way to prioritise the next 24 hours.',
  },

  /* ── Per-criterion (for each of the 10 criteria) ────────────── */
  criterionRationale: {
    title: 'Criterion Rationale',
    body: 'Claude\'s 2-3 sentence explanation of why this criterion scored what it did, based on the specific evidence in the asset record.',
  },
  criterionEvidence: {
    title: 'Supporting Evidence',
    body: 'The concrete data points Claude cited for this criterion — trial IDs, publication mentions, deal history, published mechanisms of action, market data, etc.',
  },
  criterionConfidence: {
    title: 'Criterion Confidence',
    body: 'How reliable this criterion\'s score is. Low confidence means the evidence was thin or contradictory — treat that score with caution.',
  },
};

/* ── Criterion-level help — keyed by criterion.key ─────────────── */
export const CRITERION_HELP = {
  translation_potential: {
    title: 'Animal Health Translation Potential',
    body: 'Strong biological rationale that the human asset can successfully translate into companion animals (dogs/cats), supported by conserved biology, cross-species disease similarity, and any available in-species or cross-species evidence. This is the primary gate — if translation is weak, downstream criteria must reflect that.',
  },
  disease_unmet_need: {
    title: 'Disease & Unmet Need Fit',
    body: 'Whether the asset addresses a significant unmet veterinary need with limited current treatment options and meaningful clinical value for owners and vets.',
  },
  scientific_evidence: {
    title: 'Scientific Evidence & Differentiation',
    body: 'Strength of efficacy, safety and mechanistic evidence — and how differentiated the profile is vs existing veterinary therapies.',
  },
  development_readiness: {
    title: 'Development Readiness',
    body: 'Development stage, safety package and technical maturity — does the asset substantially reduce development risk for animal health, or does the buyer still need to run costly new studies?',
  },
  commercial_opportunity: {
    title: 'Commercial Opportunity',
    body: 'Attractive market size, pricing potential, adoption potential and sustainable commercial opportunity in companion animal health.',
  },
  regulatory_feasibility: {
    title: 'Regulatory Feasibility',
    body: 'Clear, realistic and achievable veterinary regulatory pathway (FDA-CVM, EMA-CVMP, PMDA, etc.) with manageable development requirements.',
  },
  ip_exclusivity: {
    title: 'IP & Competitive Position',
    body: 'Strong intellectual property, remaining exclusivity and favourable competitive positioning with acceptable freedom to operate.',
  },
  licensing_readiness: {
    title: 'Licensing & Partner Readiness',
    body: 'Whether the asset appears available for partnering, with a realistic licensing pathway and an engaged or partnership-friendly owner.',
  },
  buyer_interest: {
    title: 'Strategic Buyer Alignment',
    body: 'Alignment with the known interests, portfolios and acquisition/licensing strategies of target animal health companies (Zoetis, Elanco, Merck AH, Boehringer AH, Ceva, Virbac, Vetoquinol, Dechra, etc.).',
  },
  strategic_fit: {
    title: 'Overall Partnering Attractiveness',
    body: 'A holistic judgment considering all evidence above — does the asset represent a compelling, investable and strategically attractive animal-health licensing opportunity?',
  },
};
